import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const KeywordAnalysis = ({ 
  keywords = [], 
  targetRole = 'Frontend Developer',
  onOptimizeKeywords 
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { key: 'all', label: 'All Keywords', icon: 'Hash' },
    { key: 'technical', label: 'Technical Skills', icon: 'Code' },
    { key: 'soft', label: 'Soft Skills', icon: 'Users' },
    { key: 'tools', label: 'Tools & Platforms', icon: 'Settings' },
    { key: 'missing', label: 'Missing Keywords', icon: 'AlertTriangle' }
  ];

  const filteredKeywords = keywords?.filter(keyword => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'missing') return keyword?.status === 'missing';
    return keyword?.category === selectedCategory;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'text-success bg-success/10 border-success/20';
      case 'partial': return 'text-warning bg-warning/10 border-warning/20';
      case 'missing': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return 'CheckCircle';
      case 'partial': return 'AlertCircle';
      case 'missing': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getDensityColor = (density) => {
    if (density >= 80) return 'text-success';
    if (density >= 60) return 'text-warning';
    return 'text-error';
  };

  const keywordStats = {
    total: keywords?.length,
    present: keywords?.filter(k => k?.status === 'present')?.length,
    partial: keywords?.filter(k => k?.status === 'partial')?.length,
    missing: keywords?.filter(k => k?.status === 'missing')?.length,
    averageDensity: Math.round(keywords?.reduce((acc, k) => acc + (k?.density || 0), 0) / keywords?.length)
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Hash" size={20} className="text-primary" />
            <h3 className="font-semibold text-card-foreground">Keyword Analysis</h3>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="Zap"
            onClick={onOptimizeKeywords}
          >
            Auto-Optimize
          </Button>
        </div>

        {/* Target Role */}
        <div className="mb-4">
          <div className="text-sm text-muted-foreground mb-1">Target Role:</div>
          <div className="font-medium text-card-foreground">{targetRole}</div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <div className="text-lg font-bold text-success">{keywordStats?.present}</div>
            <div className="text-xs text-muted-foreground">Present</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-warning">{keywordStats?.partial}</div>
            <div className="text-xs text-muted-foreground">Partial</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-error">{keywordStats?.missing}</div>
            <div className="text-xs text-muted-foreground">Missing</div>
          </div>
          <div className="text-center">
            <div className={`text-lg font-bold ${getDensityColor(keywordStats?.averageDensity)}`}>
              {keywordStats?.averageDensity}%
            </div>
            <div className="text-xs text-muted-foreground">Avg Density</div>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          {categories?.map(category => (
            <button
              key={category?.key}
              onClick={() => setSelectedCategory(category?.key)}
              className={`flex items-center space-x-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${
                selectedCategory === category?.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={category?.icon} size={14} />
              <span>{category?.label}</span>
              <span className="opacity-75">
                ({category?.key === 'all' ? keywordStats?.total : 
                  category?.key === 'missing' ? keywordStats?.missing :
                  keywords?.filter(k => k?.category === category?.key)?.length})
              </span>
            </button>
          ))}
        </div>
      </div>
      {/* Keywords List */}
      <div className="max-h-80 overflow-y-auto">
        {filteredKeywords?.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="Search" size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No keywords found for {selectedCategory === 'all' ? 'this filter' : selectedCategory}
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {filteredKeywords?.map((keyword) => (
              <div 
                key={keyword?.id}
                className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors duration-200"
              >
                <div className="flex items-center space-x-3 flex-1">
                  <Icon 
                    name={getStatusIcon(keyword?.status)} 
                    size={16} 
                    className={getStatusColor(keyword?.status)?.split(' ')?.[0]} 
                  />
                  <div className="flex-1">
                    <div className="font-medium text-card-foreground text-sm">
                      {keyword?.term}
                    </div>
                    {keyword?.context && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {keyword?.context}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {/* Importance */}
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Importance</div>
                    <div className="flex items-center space-x-1">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={12}
                          className={i < keyword?.importance ? 'text-warning fill-current' : 'text-muted-foreground'}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Density */}
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Density</div>
                    <div className={`text-sm font-medium ${getDensityColor(keyword?.density || 0)}`}>
                      {keyword?.density || 0}%
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(keyword?.status)}`}>
                    {keyword?.status}
                  </div>

                  {/* Action */}
                  {keyword?.status === 'missing' && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Plus"
                      onClick={() => {/* Add keyword to resume */}}
                    >
                      Add
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            Showing {filteredKeywords?.length} of {keywords?.length} keywords
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-success font-medium">
              +{keywordStats?.present * 2}% potential match improvement
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeywordAnalysis;